const RAT_TEXT_RE = /^-?(0|[1-9]\d*)(\/[1-9]\d*)?$/;
const MAX_RADICAND = 10n ** 12n;
const MAX_POW = 4096;
function babs(a) {
  return a < 0n ? -a : a;
}
function gcd(a, b) {
  a = babs(a);
  b = babs(b);
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}
function toBig(v) {
  if (typeof v === 'bigint') return v;
  if (!Number.isSafeInteger(v)) throw new RangeError(`entier attendu : ${v}`);
  return BigInt(v);
}
function rat(n, d = 1n) {
  let nn = toBig(n);
  let dd = toBig(d);
  if (dd === 0n) throw new RangeError('d\xE9nominateur nul');
  if (dd < 0n) {
    nn = -nn;
    dd = -dd;
  }
  const g = gcd(nn, dd) || 1n;
  return { n: nn / g, d: dd / g };
}
const ZERO = rat(0);
const ONE = rat(1);
function add(a, b) {
  return rat(a.n * b.d + b.n * a.d, a.d * b.d);
}
function sub(a, b) {
  return rat(a.n * b.d - b.n * a.d, a.d * b.d);
}
function mul(a, b) {
  return rat(a.n * b.n, a.d * b.d);
}
function div(a, b) {
  if (b.n === 0n) return null;
  return rat(a.n * b.d, a.d * b.n);
}
function neg(a) {
  return { n: -a.n, d: a.d };
}
function abs(a) {
  return a.n < 0n ? neg(a) : a;
}
function sign(a) {
  return a.n === 0n ? 0 : a.n < 0n ? -1 : 1;
}
function cmp(a, b) {
  const x = a.n * b.d - b.n * a.d;
  return x === 0n ? 0 : x < 0n ? -1 : 1;
}
function eq(a, b) {
  return a.n === b.n && a.d === b.d;
}
function isZero(a) {
  return a.n === 0n;
}
function isInteger(a) {
  return a.d === 1n;
}
function pow(a, k) {
  if (!Number.isInteger(k) || Math.abs(k) > MAX_POW) return null;
  if (k < 0) {
    if (a.n === 0n) return null;
    return pow(rat(a.d, a.n), -k);
  }
  const e = BigInt(k);
  return rat(a.n ** e, a.d ** e);
}
function ratToText(a) {
  return a.d === 1n ? a.n.toString() : `${a.n}/${a.d}`;
}
function ratFromText(s) {
  if (!RAT_TEXT_RE.test(s)) return null;
  const [p, q] = s.split('/');
  return rat(BigInt(p), q === void 0 ? 1n : BigInt(q));
}
const DECIMAL_RE = /^([+\-−]?)(\d{1,40})(?:[.,](\d{1,40}))?$/;
function parseDecimal(text) {
  const m = DECIMAL_RE.exec(text.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const n = BigInt(m[2] + frac);
  const r = rat(n, 10n ** BigInt(frac.length));
  return m[1] === '-' || m[1] === '\u2212' ? neg(r) : r;
}
function ratToNumber(a) {
  return Number(a.n) / Number(a.d);
}
function squarefreePart(n) {
  if (n < 1n || n > MAX_RADICAND) return null;
  let m = Number(n);
  let square = 1;
  let free = 1;
  for (let p = 2; p * p <= m; p += p === 2 ? 1 : 2) {
    let e = 0;
    while (m % p === 0) {
      m /= p;
      e++;
    }
    if (e > 0) {
      square *= p ** Math.floor(e / 2);
      if (e % 2 === 1) free *= p;
    }
  }
  if (m > 1) free *= m;
  return { square: BigInt(square), free: BigInt(free) };
}
function radical(coef, rad) {
  if (rad < 0n) return null;
  if (coef.n === 0n || rad === 0n) return { coef: ZERO, rad: 1n };
  const sf = squarefreePart(rad);
  if (!sf) return null;
  return { coef: mul(coef, rat(sf.square)), rad: sf.free };
}
function fromRat(q) {
  return { coef: q, rad: 1n };
}
function sqrtRat(q) {
  if (q.n < 0n) return null;
  return radical(rat(1n, q.d), q.n * q.d);
}
function radNeg(a) {
  return { coef: neg(a.coef), rad: a.rad };
}
function radMul(a, b) {
  return radical(mul(a.coef, b.coef), a.rad * b.rad);
}
function radAdd(a, b) {
  if (a.coef.n === 0n) return b;
  if (b.coef.n === 0n) return a;
  if (a.rad !== b.rad) return null;
  return radical(add(a.coef, b.coef), a.rad);
}
function radEq(a, b) {
  return eq(a.coef, b.coef) && (a.coef.n === 0n || a.rad === b.rad);
}
function radIsRational(a) {
  return a.rad === 1n || a.coef.n === 0n;
}
function radCmpRat(a, q) {
  if (a.rad === 1n || a.coef.n === 0n) return cmp(a.coef, q);
  const sa = sign(a.coef);
  const sq = sign(q);
  if (sa !== sq) return sa > sq ? 1 : -1;
  const left = mul(mul(a.coef, a.coef), rat(a.rad));
  const right = mul(q, q);
  const c = cmp(left, right);
  return sa > 0 ? c : -c;
}
function isRoundingOf(value, approx, places) {
  if (!Number.isInteger(places) || places < 0 || places > 30) return false;
  const half = rat(1n, 2n * 10n ** BigInt(places));
  return radCmpRat(value, sub(approx, half)) >= 0 && radCmpRat(value, add(approx, half)) <= 0;
}
function ratMath(a) {
  const s = a.n < 0n ? '\u2212' : '';
  const n = babs(a.n).toString();
  return a.d === 1n ? s + n : `${s}${n} \xF7 ${a.d}`;
}
function radToText(a) {
  if (radIsRational(a)) return ratMath(a.coef);
  const s = a.coef.n < 0n ? '\u2212' : '';
  const p = babs(a.coef.n);
  const root = `\u221A${a.rad}`;
  const head = p === 1n ? root : `${p}${root}`;
  return a.coef.d === 1n ? s + head : `${s}${head} \xF7 ${a.coef.d}`;
}
export {
  MAX_RADICAND,
  ONE,
  RAT_TEXT_RE,
  ZERO,
  abs,
  add,
  cmp,
  div,
  eq,
  fromRat,
  isInteger,
  isRoundingOf,
  isZero,
  mul,
  neg,
  parseDecimal,
  pow,
  radAdd,
  radCmpRat,
  radEq,
  radIsRational,
  radMul,
  radNeg,
  radToText,
  radical,
  rat,
  ratFromText,
  ratToNumber,
  ratToText,
  sign,
  sqrtRat,
  squarefreePart,
  sub,
};
